import { Link } from "react-router-dom";
import { FormatChips } from "@/components/Badges";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { inr, fileSize, multiCurrencyLine } from "@/lib/api";
import { toast } from "sonner";
import { Eye, Box, ShoppingCart, Check } from "lucide-react";
import SafeImage from "@/components/SafeImage";

export default function ListingCard({ listing }) {
  const { add, has } = useCart();
  const { rates } = useCurrency();
  return (
    <Link
      to={`/design/${listing.id}`}
      data-testid={`listing-card-${listing.id}`}
      className="group flex flex-col border border-border rounded-md bg-card overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:border-primary hover:shadow-lg"
    >
      <div className="relative aspect-[4/3] bg-slate-900 mesh-grid flex items-center justify-center overflow-hidden">
        {listing.cover_image ? (
          <SafeImage
            src={`${listing.cover_image}${listing.cover_image.includes("?") ? "&" : "?"}w=480`}
            alt={listing.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            testid={`listing-card-img-${listing.id}`}
            imgProps={{ width: "480", height: "360" }}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-primary/50">
            <Box className="h-10 w-10 sm:h-14 sm:w-14" strokeWidth={1} />
            <p className="text-[10px] uppercase tracking-widest font-mono opacity-75">Preview pending</p>
          </div>
        )}
        <div className="absolute top-1.5 left-1.5 sm:top-2.5 sm:left-2.5 z-10 scale-[0.85] sm:scale-100 origin-top-left">
          <FormatChips listing={listing} />
        </div>
        {listing.featured && (
          <span className="absolute top-1.5 right-1.5 sm:top-2.5 sm:right-2.5 z-10 font-mono text-[8px] sm:text-[9px] uppercase tracking-widest px-1.5 sm:px-2 py-0.5 rounded text-slate-900" style={{ backgroundColor: "hsl(var(--gold))" }}>
            Featured
          </span>
        )}
      </div>
      <div className="p-2.5 sm:p-4 flex flex-col gap-2 sm:gap-3 flex-1">
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2 mb-0.5 sm:mb-1">
            <p className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground truncate">{listing.category_name}</p>
            {listing.product_id && (
              <span
                data-testid={`card-pid-${listing.id}`}
                className="text-[8px] sm:text-[9px] font-mono font-semibold px-1.5 py-0.5 rounded bg-secondary/60 text-muted-foreground shrink-0"
                title="Jeweltix Product ID"
              >
                {listing.product_id}
              </span>
            )}
          </div>
          <h3 className="font-display font-semibold text-xs sm:text-sm leading-snug line-clamp-2 group-hover:text-primary transition-colors">
            {listing.title}
          </h3>
          <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 truncate">{listing.seller?.name}</p>
        </div>
        {/* Meta grid — hidden on smallest screens for Blinkit-style compact cards */}
        <div className="hidden sm:grid grid-cols-3 gap-2 pt-3 border-t border-border font-mono text-[10px] text-muted-foreground">
          <div>
            <div className="text-foreground uppercase tracking-wider">{listing.file_type}</div>
            format
          </div>
          <div>
            <div className="text-foreground">{fileSize(listing.file_size)}</div>
            size
          </div>
          <div>
            <div className="text-foreground">{(listing.poly_count / 1000).toFixed(1)}K</div>
            polys
          </div>
        </div>
        {listing.weights?.gold_18k && (
          <div className="hidden sm:block font-mono text-[10px]" style={{ color: "hsl(var(--gold))" }} data-testid="card-weight">
            18K ≈ {listing.weights.gold_18k} g · Silver ≈ {listing.weights.silver} g
          </div>
        )}
        {listing.length_mm > 0 && (
          <div className="hidden sm:block font-mono text-[10px] text-muted-foreground" data-testid={`card-dims-${listing.id}`}>
            {listing.length_mm} × {listing.width_mm} × {listing.height_mm} mm
            {listing.ring_size_indian ? ` · Ring ${listing.ring_size_indian}` : ""}
            {listing.bangle_size_indian ? ` · ${listing.bangle_size_indian}` : ""}
          </div>
        )}
        <div className="flex items-center justify-between pt-1 sm:pt-2">
          <div className="flex flex-col min-w-0">
            <span className="font-display font-bold text-sm sm:text-lg leading-none">{inr(listing.price)}</span>
            {rates && (
              <span className="hidden sm:inline font-mono text-[10px] text-muted-foreground mt-1" data-testid={`card-multi-currency-${listing.id}`}>
                {multiCurrencyLine(listing.price, rates)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <span className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground font-mono">
              <Eye className="h-3.5 w-3.5" /> {listing.views}
            </span>
            <button
              data-testid={`card-add-cart-${listing.id}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (has(listing.id)) return; add(listing); toast.success("Added to cart"); }}
              title="Add to cart"
              className="p-1.5 sm:p-1.5 rounded-md border border-border hover:border-primary hover:text-primary transition-colors"
            >
              {has(listing.id) ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <ShoppingCart className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}
